import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function policyHint(p) {
  if (!p) return "";
  const bits = [`${p.min_len}+ chars`];
  if (p.mixed_case) bits.push("mixed case");
  if (p.digit_or_symbol) bits.push("digit or symbol");
  return bits.join(" · ");
}

export default function ChangePasswordDialog({ open, onOpenChange, forced = false }) {
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/auth/password-policy`)
      .then((r) => setPolicy(r.data))
      .catch(() => {});
  }, [open]);

  const rolePolicy = user && user !== false ? policy?.[user.role] : null;

  const submit = async () => {
    if (next.length < 6) return toast.error("Password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      await refresh();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!forced) onOpenChange(v); }}>
      <DialogContent data-testid="change-password-dialog" onInteractOutside={forced ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle>{forced ? "Set a new password" : "Change password"}</DialogTitle>
        </DialogHeader>
        {forced && (
          <p className="text-sm text-zinc-500">
            Your admin issued you a temporary password. Please set your own before continuing.
          </p>
        )}
        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label>Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" />
          </div>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" />
            {rolePolicy && (
              <p className="text-xs text-zinc-500" data-testid="cp-policy-hint">
                Policy for {user?.role}: {policyHint(rolePolicy)}.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" />
          </div>
        </div>
        <DialogFooter>
          {forced ? (
            <Button variant="ghost" onClick={logout}>Sign out</Button>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          )}
          <Button onClick={submit} disabled={loading} data-testid="cp-submit">
            {loading ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
